'use client';

import { useState, useEffect, useRef } from 'react';
import SubjectPicker from '@/app/components/SubjectPicker';
import ChatMessage from '@/app/components/ChatMessage';
import ChatInput from '@/app/components/ChatInput';
import ChildSelector from '@/app/components/ChildSelector';
import CalendarView from '@/app/components/CalendarView';
import PinGate from '@/app/components/PinGate';
import { useChat } from '@/app/hooks/use-chat';
import {
  SUBJECTS,
} from '@/app/lib/tutor-config';
import {
  PROFILES,
  getStoredProfileId,
  setStoredProfileId,
  clearStoredProfile,
  getProfileById,
} from '@/app/lib/profiles';

type SubjectDocument = {
  filename: string;
  originalName: string;
  uploadedAt: string;
};

export type CalendarAssignment = {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  subject: string;
  calendarUid: string;
  location?: string;
  subjectOverride?: string;
};

export default function Home() {
  const [authChecked, setAuthChecked] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [profileReady, setProfileReady] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [documentsBySubject, setDocumentsBySubject] = useState<
    Record<string, SubjectDocument[]>
  >({});
  const [calendarConfig, setCalendarConfig] = useState<{
    calendarUrl: string;
    lastSyncedAt: string | null;
  } | null>(null);
  const [assignments, setAssignments] = useState<CalendarAssignment[]>([]);
  const [viewMode, setViewMode] = useState<'chat' | 'calendar'>('chat');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const profile = profileId ? getProfileById(profileId) : null;
  const subjects = profile
    ? profile.subjectIds
      ? SUBJECTS.filter((s) => profile.subjectIds!.includes(s.id))
      : SUBJECTS
    : SUBJECTS;

  const currentSubject = selectedSubject
    ? subjects.find((s) => s.id === selectedSubject)
    : null;

  const { messages, isLoading, sendMessage, stopGeneration, clearChat } = useChat(
    selectedSubject,
    currentSubject ? currentSubject.name : null,
    profile
      ? {
          profileId: profile.id,
          studentName: profile.name,
          gradeLevel: profile.gradeLevel,
          studyStyle: profile.studyStyle,
        }
      : null
  );

  // Check PIN auth on mount
  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth')
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setAuthRequired(Boolean(data.required));
        setIsAuthenticated(Boolean(data.authenticated));
      })
      .catch(() => {
        if (cancelled) return;
        setAuthRequired(false);
        setIsAuthenticated(true);
      })
      .finally(() => {
        if (!cancelled) setAuthChecked(true);
      });
    return () => { cancelled = true; };
  }, []);

  // Restore profile from localStorage on mount
  useEffect(() => {
    const stored = getStoredProfileId();
    if (stored && getProfileById(stored)) {
      setProfileId(stored);
    }
    setProfileReady(true);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!profileId || !selectedSubject) return;
    const loadDocumentsForSubject = async () => {
      try {
        const res = await fetch(
          `/api/documents?profile=${encodeURIComponent(profileId)}&subject=${encodeURIComponent(selectedSubject)}`
        );
        if (!res.ok) return;
        const data = await res.json();
        setDocumentsBySubject((prev) => ({
          ...prev,
          [selectedSubject]: data.documents ?? [],
        }));
      } catch (error) {
        console.error('Failed to load documents for subject', selectedSubject, error);
      }
    };

    loadDocumentsForSubject();
  }, [profileId, selectedSubject]);

  useEffect(() => {
    if (!profileId) {
      setCalendarConfig(null);
      setAssignments([]);
      return;
    }
    const loadCalendar = async () => {
      try {
        const [configRes, assignRes] = await Promise.all([
          fetch(`/api/calendar/config?profile=${encodeURIComponent(profileId)}`),
          fetch(`/api/calendar/assignments?profile=${encodeURIComponent(profileId)}`),
        ]);
        if (configRes.ok) {
          const d = await configRes.json();
          setCalendarConfig({ calendarUrl: d.calendarUrl ?? '', lastSyncedAt: d.lastSyncedAt ?? null });
        }
        if (assignRes.ok) {
          const d = await assignRes.json();
          setAssignments(d.assignments ?? []);
        }
      } catch (e) {
        console.error('Failed to load calendar data', e);
      }
    };
    loadCalendar();
  }, [profileId]);

  const handleSelectProfile = (selectedProfile: { id: string }) => {
    setStoredProfileId(selectedProfile.id);
    setProfileId(selectedProfile.id);
    setSelectedSubject(null);
    setDocumentsBySubject({});
    setCalendarConfig(null);
    setAssignments([]);
  };

  const handleSwitchUser = () => {
    clearStoredProfile();
    setProfileId(null);
    setSelectedSubject(null);
    setDocumentsBySubject({});
    clearChat();
    setSidebarOpen(false);
  };

  const handleNewChat = () => {
    clearChat();
    setSidebarOpen(false);
  };

  const handleSubjectSelect = (subjectId: string | null) => {
    setSelectedSubject(subjectId);
    setSidebarOpen(false);
  };

  const handleDocumentsChange = (subjectId: string, documents: SubjectDocument[]) => {
    setDocumentsBySubject((prev) => ({
      ...prev,
      [subjectId]: documents,
    }));
  };

  const handleDeleteDocument = async (subjectId: string, filename: string) => {
    if (!profileId) return;
    try {
      const res = await fetch(
        `/api/documents?profile=${encodeURIComponent(profileId)}&subject=${encodeURIComponent(subjectId)}&file=${encodeURIComponent(filename)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        console.error('Failed to delete document', await res.text());
        return;
      }

      const refreshed = await fetch(
        `/api/documents?profile=${encodeURIComponent(profileId)}&subject=${encodeURIComponent(subjectId)}`
      );
      if (!refreshed.ok) return;
      const data = await refreshed.json();
      setDocumentsBySubject((prev) => ({
        ...prev,
        [subjectId]: data.documents ?? [],
      }));
    } catch (error) {
      console.error('Failed to delete document', error);
    }
  };

  const handleSaveCalendarUrl = async () => {
    const res = await fetch(`/api/calendar/config?profile=${encodeURIComponent(profileId!)}`);
    if (res.ok) {
      const d = await res.json();
      setCalendarConfig({ calendarUrl: d.calendarUrl ?? '', lastSyncedAt: d.lastSyncedAt ?? null });
    }
  };

  const handleRefreshCalendar = async () => {
    if (!profileId) return;
    try {
      const res = await fetch('/api/calendar/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.error || 'Sync failed');
        return;
      }
      setAssignments(data.assignments ?? []);
      if (data.lastSyncedAt) {
        setCalendarConfig((prev) => (prev ? { ...prev, lastSyncedAt: data.lastSyncedAt } : null));
      }
    } catch (e) {
      alert('Sync failed');
    }
  };

  const handleUpdateAssignmentSubject = async (assignmentId: string, subject: string) => {
    if (!profileId) return;
    try {
      const res = await fetch('/api/calendar/assignments', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileId, assignmentId, subject }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setAssignments((prev) =>
        prev.map((a) => (a.id === assignmentId ? { ...a, subjectOverride: subject } : a))
      );
    } catch (e) {
      console.error('Failed to update assignment', e);
    }
  };

  // PIN gate: show before anything else when auth is required and not yet authenticated
  if (authChecked && authRequired && !isAuthenticated) {
    return <PinGate onSuccess={() => setIsAuthenticated(true)} />;
  }

  // Loading until auth and profile state are ready
  if (!authChecked || !profileReady) {
    return (
      <div className="app">
        <div className="child-selector">
          <div className="child-selector__card">
            <p className="child-selector__subtitle">Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="app">
        <ChildSelector profiles={PROFILES} onSelect={handleSelectProfile} />
      </div>
    );
  }

  return (
    <div className="app">
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <div className={`sidebar-wrapper ${sidebarOpen ? 'sidebar-wrapper--open' : ''}`}>
        <SubjectPicker
          selected={selectedSubject}
          onSelect={handleSubjectSelect}
          onNewChat={handleNewChat}
          onSwitchUser={handleSwitchUser}
          subjects={subjects}
          studentName={profile.name}
          documentsBySubject={documentsBySubject}
          onDeleteDocument={handleDeleteDocument}
          profileId={profileId}
          calendarConfig={calendarConfig}
          assignments={assignments}
          onSaveCalendarUrl={handleSaveCalendarUrl}
          onRefreshCalendar={handleRefreshCalendar}
          onUpdateAssignmentSubject={handleUpdateAssignmentSubject}
          viewMode={viewMode}
          onViewChange={setViewMode}
        />
      </div>

      <main className="main">
        <div className="mobile-header">
          <button className="mobile-header__menu" onClick={() => setSidebarOpen(true)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <span className="mobile-header__title">
            {viewMode === 'calendar'
              ? `${profile.name}'s Calendar`
              : currentSubject
                ? `${currentSubject.emoji} ${currentSubject.name}`
                : `💡 ${profile.name}'s Study Buddy`}
          </span>
        </div>

        {viewMode === 'calendar' ? (
          <div className="calendar-area">
            <CalendarView
              profileId={profileId!}
              assignments={assignments}
              subjects={subjects}
              studentName={profile.name}
            />
          </div>
        ) : (
        <>
        <div className="chat-area">
          {messages.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <h2 className="empty-state__title">What are you studying?</h2>
              <p className="empty-state__text">
                Pick a subject from the sidebar, then ask me anything. I&apos;ll help you work through it &mdash; not just give you the answer.
              </p>
              <div className="empty-state__suggestions">
                {[
                  'Help me understand how photosynthesis works',
                  'Quiz me on my Spanish vocabulary',
                  "I'm stuck on this math problem...",
                  'Explain the causes of World War I',
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    className="empty-state__suggestion"
                    onClick={() => sendMessage(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="messages">
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))}
              {isLoading && messages[messages.length - 1]?.content === '' && (
                <div className="typing-indicator">
                  <span /><span /><span />
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        <ChatInput
          onSend={sendMessage}
          isLoading={isLoading}
          onStop={stopGeneration}
          subjectId={selectedSubject}
          profileId={profileId}
          onDocumentsChange={handleDocumentsChange}
        />
        </>
        )}
      </main>
    </div>
  );
}
